import React, { useState, useEffect, createRef } from 'react';
import { Grid, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { FormInput, FormNumericTextBox, FormDatePicker } from '../FormComponents';
import { requiredValidator } from '../Validators';
import { 
  VehicleCost, 
  getVehicleCostsByVehicle, 
  createVehicleCost, 
  updateVehicleCost, 
  deleteVehicleCost,
  getVehicleCostDocuments,
} from '../../../data/vehicleCostData';
import { deleteDocument } from '../../../data/fileData';
import { FaEdit, FaTrash, FaPlusCircle, FaPaperclip, FaFilePdf, FaFileCsv, FaFileImage, FaFileAlt } from 'react-icons/fa';
import { Upload, UploadOnStatusChangeEvent, UploadFileInfo } from '@progress/kendo-react-upload';
import dayjs from 'dayjs';

const apiURL = process.env.REACT_APP_API_URL;

interface VehicleCostsProps {
  vehicleId: number;
  /** False without edit access: costs and their documents are only shown. */
  canEdit?: boolean;
}

interface CostDocument {
  documentId: number;
  fileName: string;
  fileExtension: string;
  appCanView: boolean;
}

const VehicleCosts: React.FC<VehicleCostsProps> = ({ vehicleId, canEdit = true }) => {
  const [costs, setCosts] = useState<VehicleCost[]>([]);
  const [loading, setLoading] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [selectedCost, setSelectedCost] = useState<VehicleCost | null>(null);
  const [isNew, setIsNew] = useState(false);

  // Document management state
  const [docsDialogVisible, setDocsDialogVisible] = useState(false);
  const [selectedCostForDocs, setSelectedCostForDocs] = useState<VehicleCost | null>(null);
  const [costDocuments, setCostDocuments] = useState<CostDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFileInfo[]>([]);
  const uploadRef = createRef<Upload>();

  const token = localStorage.getItem('authToken');
  const storedApiURL = localStorage.getItem('apiURL');
  const finalApiURL = storedApiURL?.replace('https://', '').replace('http://', '') || apiURL || 'localhost:7048';

  useEffect(() => {
    loadCosts();
  }, [vehicleId]);

  const loadCosts = async () => {
    try {
      setLoading(true);
      const data = await getVehicleCostsByVehicle(vehicleId);
      setCosts(data);
    } catch (error) {
      console.error('Error loading vehicle costs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedCost({
      vehicleId,
      description: '',
      cost: 0,
      date: new Date(),
    });
    setIsNew(true);
    setEditDialogVisible(true);
  };

  const handleEdit = (cost: VehicleCost) => {
    setSelectedCost({ ...cost });
    setIsNew(false);
    setEditDialogVisible(true);
  };

  const handleDeleteClick = (cost: VehicleCost) => {
    setSelectedCost(cost);
    setDeleteDialogVisible(true);
  };

  const handleSave = async (dataItem: any) => {
    try {
      const costData: VehicleCost = {
        vehicleId,
        description: dataItem.description,
        cost: dataItem.cost,
        date: dataItem.date ? new Date(dataItem.date).toISOString() : new Date().toISOString(),
      };

      if (isNew) {
        await createVehicleCost(costData);
      } else if (selectedCost?.vehicleCostId) {
        costData.vehicleCostId = selectedCost.vehicleCostId;
        await updateVehicleCost(selectedCost.vehicleCostId, costData);
      }

      await loadCosts();
      setEditDialogVisible(false);
    } catch (error) {
      console.error('Error saving vehicle cost:', error);
      alert('Failed to save vehicle cost');
    }
  };

  const handleDelete = async () => {
    if (!selectedCost?.vehicleCostId) return;

    try {
      await deleteVehicleCost(selectedCost.vehicleCostId);
      await loadCosts();
      setDeleteDialogVisible(false);
    } catch (error) {
      console.error('Error deleting vehicle cost:', error);
      alert('Failed to delete vehicle cost');
    }
  };

  // Document management handlers
  const handleOpenDocs = async (cost: VehicleCost) => {
    setSelectedCostForDocs(cost);
    setUploadFiles([]);
    setDocsDialogVisible(true);
    await loadCostDocuments(cost.vehicleCostId!);
  };

  const loadCostDocuments = async (vehicleCostId: number) => {
    try {
      setDocsLoading(true);
      const docs = await getVehicleCostDocuments(vehicleCostId);
      setCostDocuments(docs);
    } catch (error) {
      console.error('Error loading cost documents:', error);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleDocumentDelete = async (documentId: number) => {
    try {
      await deleteDocument(documentId);
      if (selectedCostForDocs?.vehicleCostId) {
        await loadCostDocuments(selectedCostForDocs.vehicleCostId);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleUploadStatusChange = (event: UploadOnStatusChangeEvent) => {
    setUploadFiles(event.newState);
    if (selectedCostForDocs?.vehicleCostId) {
      loadCostDocuments(selectedCostForDocs.vehicleCostId);
    }
  };

  const fileIcon = (ext: string) => {
    switch (ext.toLowerCase()) {
      case '.pdf': return <FaFilePdf style={{ fontSize: '18pt', color: '#1e2a4e', marginRight: '0.5rem' }} />;
      case '.csv': return <FaFileCsv style={{ fontSize: '18pt', color: '#1e2a4e', marginRight: '0.5rem' }} />;
      case '.jpg':
      case '.jpeg':
      case '.png': return <FaFileImage style={{ fontSize: '18pt', color: '#1e2a4e', marginRight: '0.5rem' }} />;
      default: return <FaFileAlt style={{ fontSize: '18pt', color: '#1e2a4e', marginRight: '0.5rem' }} />;
    }
  };

  const ActionsCell = (props: any) => {
    const cost = props.dataItem;
    return (
      <td className="k-command-cell">
        {canEdit && (
        <button
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
          onClick={() => handleEdit(cost)}
          style={{ marginRight: '0.25rem' }}
          title="Edit cost"
        >
          <FaEdit />
        </button>
        )}
        {cost.vehicleCostId && (
          <button
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-info"
            onClick={() => handleOpenDocs(cost)}
            style={{ marginRight: '0.25rem' }}
            title={canEdit ? 'Manage documents' : 'View documents'}
          >
            <FaPaperclip />
          </button>
        )}
        {canEdit && (
        <button
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-error"
          onClick={() => handleDeleteClick(cost)}
          title="Delete cost"
        >
          <FaTrash />
        </button>
        )}
      </td>
    );
  };

  const DateCell = (props: any) => {
    const date = props.dataItem.date;
    return (
      <td>
        {date ? dayjs(date).format('DD/MM/YYYY') : '-'}
      </td>
    );
  };

  const CostCell = (props: any) => {
    const cost = props.dataItem.cost;
    return (
      <td style={{ textAlign: 'right' }}>
        {cost != null ? `£${cost.toFixed(2)}` : '-'}
      </td>
    );
  };

  return (
    <div style={{ padding: '1rem' }}>
      <Grid
        data={costs}
        style={{ height: '400px' }}
      >
        <GridToolbar>
          {canEdit ? (
          <button
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
            onClick={handleAdd}
          >
            <FaPlusCircle style={{ marginRight: '0.5rem' }} />
            Add Cost
          </button>
          ) : (
            <span className='k-form-hint'>You have view-only access to vehicles.</span>
          )}
        </GridToolbar>
        <GridColumn field="date" title="Date" width="150px" cell={DateCell} />
        <GridColumn field="description" title="Description" />
        <GridColumn field="cost" title="Cost" width="150px" cell={CostCell} />
        <GridColumn title="Actions" width="170px" cell={ActionsCell} />
      </Grid>

      {canEdit && editDialogVisible && selectedCost && (
        <Dialog
          title={isNew ? 'Add Vehicle Cost' : 'Edit Vehicle Cost'}
          onClose={() => setEditDialogVisible(false)}
          width={500}
        >
          <Form
            initialValues={{
              description: selectedCost.description || '',
              cost: selectedCost.cost || 0,
              date: selectedCost.date ? new Date(selectedCost.date) : new Date(),
            }}
            onSubmit={handleSave}
            render={(formRenderProps) => (
              <FormElement>
                <fieldset className="k-form-fieldset">
                  <Field
                    id="date"
                    name="date"
                    label="Date"
                    component={FormDatePicker}
                    validator={requiredValidator}
                  />
                  <Field
                    id="description"
                    name="description"
                    label="Description"
                    component={FormInput}
                    validator={requiredValidator}
                  />
                  <Field
                    id="cost"
                    name="cost"
                    label="Cost (£)"
                    component={FormNumericTextBox}
                    validator={requiredValidator}
                    format="n2"
                    min={0}
                  />
                </fieldset>
                {isNew && (
                  <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 1rem' }}>
                    Save the cost first, then use the <FaPaperclip style={{ verticalAlign: 'middle' }} /> button to attach documents.
                  </p>
                )}
                <DialogActionsBar>
                  <button
                    type="button"
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                    onClick={() => setEditDialogVisible(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                    disabled={!formRenderProps.allowSubmit}
                  >
                    Save
                  </button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}

      {canEdit && deleteDialogVisible && selectedCost && (
        <Dialog
          title="Confirm Delete"
          onClose={() => setDeleteDialogVisible(false)}
        >
          <p>Are you sure you want to delete this cost?</p>
          <p><strong>Description:</strong> {selectedCost.description}</p>
          <p><strong>Cost:</strong> £{selectedCost.cost?.toFixed(2)}</p>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => setDeleteDialogVisible(false)}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-error"
              onClick={handleDelete}
            >
              Delete
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      {docsDialogVisible && selectedCostForDocs && (
        <Dialog
          title={`Documents — ${selectedCostForDocs.description}`}
          onClose={() => setDocsDialogVisible(false)}
          width={600}
        >
          <div style={{ padding: '1rem' }}>
            {canEdit && (
            <>
            <h4 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.95rem', color: '#333' }}>
              Upload Documents
            </h4>
            <Upload
              ref={uploadRef}
              batch={false}
              files={uploadFiles}
              multiple={true}
              defaultFiles={[]}
              autoUpload={true}
              withCredentials={false}
              saveHeaders={{ Authorization: `Bearer ${token}` }}
              saveUrl={`https://${finalApiURL}/Files/VehicleCost/${vehicleId}/${selectedCostForDocs.vehicleCostId}`}
              onStatusChange={handleUploadStatusChange}
            />
            </>
            )}

            <div style={{ marginTop: '1.25rem' }}>
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', color: '#333' }}>
                Attached Documents
              </h4>
              {docsLoading ? (
                <p style={{ color: '#666', fontSize: '0.9rem' }}>Loading…</p>
              ) : costDocuments.length > 0 ? (
                <div>
                  {costDocuments.map((doc) => (
                    <div
                      key={doc.documentId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '0.4rem',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {fileIcon(doc.fileExtension)}
                        <span style={{ fontSize: '0.9rem' }}>{doc.fileName}</span>
                      </div>
                      {canEdit && (
                      <button
                        className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-error"
                        onClick={() => handleDocumentDelete(doc.documentId)}
                        title="Delete document"
                      >
                        <FaTrash />
                      </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#d8e5ed',
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    color: '#444',
                  }}
                >
                  No documents attached to this cost line yet.
                </div>
              )}
            </div>
          </div>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => setDocsDialogVisible(false)}
            >
              Close
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default VehicleCosts;
